/**
 * TenantCaretakerManageModal — wspólny modal zarządzania opiekunem tenanta (H2).
 *
 * Reużywany w dwóch miejscach:
 *  - karta „Opiekun tenanta" w szczegółach (TenantCaretakerCard),
 *  - komórka „Opiekun" na liście tenantów (AdminTenants) — klik super-admina.
 *
 * Przypisywanie (dropdown adminów) + usuwanie (z potwierdzeniem). Po każdej zmianie
 * invaliduje zarówno per-tenant listę opiekunów, jak i listę tenantów (kolumna „Opiekun”
 * odświeża się sama). Akcje tylko dla adminów z uprawnieniem tenants.caretakers.manage (D2).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface Caretaker {
  id: string;
  adminUserId: string;
  adminName: string | null;
  adminEmail: string;
}
interface CaretakersResponse {
  caretakers: Caretaker[];
}
interface AdminOption {
  id: string;
  email: string;
  name: string | null;
}
interface AdminsResponse {
  admins: AdminOption[];
}

export interface TenantCaretakerManageModalProps {
  /** Tenant, którego opiekunem zarządzamy. `null` gdy modal zamknięty (brak zapytań). */
  tenantId: string | null;
  open: boolean;
  onClose: () => void;
}

export function TenantCaretakerManageModal({
  tenantId,
  open,
  onClose,
}: TenantCaretakerManageModalProps) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canManageCaretakers = hasPermission('tenants.caretakers.manage');

  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [pendingRemove, setPendingRemove] = useState<Caretaker | null>(null);

  const active = open && !!tenantId;

  const { data } = useQuery<CaretakersResponse>({
    queryKey: ['admin', 'tenant', tenantId, 'caretakers'],
    queryFn: async () =>
      (await api.get<CaretakersResponse>(`/admin/tenants/${tenantId}/caretakers`)).data,
    enabled: active,
  });

  const { data: adminsData } = useQuery<AdminsResponse>({
    queryKey: ['admin', 'admins', 'for-caretaker'],
    queryFn: async () => (await api.get<AdminsResponse>('/admin/admins?limit=100')).data,
    enabled: active && canManageCaretakers,
    staleTime: 60_000,
  });

  const caretakers = useMemo(() => data?.caretakers ?? [], [data]);
  const assignedIds = useMemo(() => new Set(caretakers.map((c) => c.adminUserId)), [caretakers]);

  const adminOptions = useMemo(() => {
    const opts = [{ value: '', label: t('admin.tenants.detail.caretaker.assignPlaceholder') }];
    for (const a of adminsData?.admins ?? []) {
      if (assignedIds.has(a.id)) continue;
      opts.push({ value: a.id, label: a.name ? `${a.name} (${a.email})` : a.email });
    }
    return opts;
  }, [adminsData, assignedIds, t]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId, 'caretakers'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
  };

  const assignMutation = useMutation({
    mutationFn: async (adminUserId: string) =>
      api.post(`/admin/tenants/${tenantId}/caretakers`, { adminUserId }),
    onSuccess: () => {
      setSelectedAdminId('');
      invalidate();
      toast.show({ variant: 'success', title: t('admin.tenants.detail.caretaker.assignedToast') });
    },
    onError: () =>
      toast.show({ variant: 'error', title: t('admin.tenants.detail.caretaker.assignError') }),
  });

  const removeMutation = useMutation({
    mutationFn: async (caretakerId: string) =>
      api.delete(`/admin/tenants/${tenantId}/caretakers/${caretakerId}`),
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('admin.tenants.detail.caretaker.removedToast') });
    },
    onError: () =>
      toast.show({ variant: 'error', title: t('admin.tenants.detail.caretaker.removeError') }),
  });

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={t('admin.tenants.detail.caretaker.modalTitle')}
        size="md"
        footer={
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('admin.tenants.detail.caretaker.close')}
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          {caretakers.length === 0 ? (
            <p className="text-sm text-text-muted">{t('admin.tenants.detail.caretaker.empty')}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {caretakers.map((c) => (
                <li
                  key={c.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border-secondary px-3 py-2"
                >
                  <span className="text-sm text-text-primary">
                    {c.adminName || c.adminEmail}
                    {c.adminName && (
                      <span className="ml-2 text-xs text-text-tertiary">{c.adminEmail}</span>
                    )}
                  </span>
                  {canManageCaretakers && (
                    <Button variant="ghost" size="sm" onClick={() => setPendingRemove(c)}>
                      {t('admin.tenants.detail.caretaker.remove')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {canManageCaretakers && (
            <div className="flex flex-wrap items-end gap-3 border-t border-border-secondary pt-4">
              <FormField
                label={t('admin.tenants.detail.caretaker.assignLabel')}
                className="sm:w-72"
              >
                <Dropdown
                  value={selectedAdminId}
                  onChange={(v) => setSelectedAdminId(v)}
                  options={adminOptions}
                />
              </FormField>
              <Button
                variant="primary"
                size="sm"
                disabled={!selectedAdminId || assignMutation.isPending}
                onClick={() => selectedAdminId && assignMutation.mutate(selectedAdminId)}
              >
                {t('admin.tenants.detail.caretaker.assignButton')}
              </Button>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={pendingRemove !== null}
        variant="danger"
        title={t('admin.tenants.detail.caretaker.removeConfirmTitle')}
        message={
          pendingRemove
            ? t('admin.tenants.detail.caretaker.removeConfirmMessage', {
                name: pendingRemove.adminName || pendingRemove.adminEmail,
              })
            : ''
        }
        confirmLabel={t('admin.tenants.detail.caretaker.remove')}
        onConfirm={() => {
          if (pendingRemove) removeMutation.mutate(pendingRemove.id);
          setPendingRemove(null);
        }}
        onClose={() => setPendingRemove(null)}
      />
    </>
  );
}
